import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fmtErr } from "@/lib/api";
import { isNetworkError } from "@/lib/scanFx";

const read = (key) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch (_) { return []; } };
const write = (key, items) => localStorage.setItem(key, JSON.stringify(items));

/**
 * Antrean scan offline di localStorage. `send(item)` harus melempar error bila gagal.
 * Item dihapus dari antrean bila sukses ATAU ditolak server (4xx); tetap tersimpan bila error jaringan.
 */
export function useOfflineQueue(key, send, onSynced) {
  const [pending, setPending] = useState(() => read(key));
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const busy = useRef(false);

  const add = useCallback((item) => {
    const next = [...read(key), { ...item, queuedAt: new Date().toISOString(), qid: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }];
    write(key, next);
    setPending(next);
  }, [key]);

  const remove = useCallback((qid) => {
    const next = read(key).filter((x) => x.qid !== qid);
    write(key, next);
    setPending(next);
  }, [key]);

  const sync = useCallback(async () => {
    if (busy.current) return;
    const items = read(key);
    if (!items.length) return;
    busy.current = true;
    setSyncing(true);
    let ok = 0, rejected = 0;
    for (const it of items) {
      try {
        await send(it);
        ok += 1;
        remove(it.qid);
      } catch (e) {
        if (isNetworkError(e)) break;
        rejected += 1;
        remove(it.qid);
        toast.error(`${it.code}: ${fmtErr(e)}`);
      }
    }
    busy.current = false;
    setSyncing(false);
    if (ok || rejected) {
      toast[ok ? "success" : "warning"](`Sinkron offline: ${ok} terkirim${rejected ? ` · ${rejected} ditolak` : ""}`);
      if (onSynced) onSynced();
    }
  }, [key, send, remove, onSynced]);

  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    if (navigator.onLine) sync();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, [sync]);

  return { pending, add, remove, sync, syncing, online };
}
